import { useState, useEffect } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import classNames from "classnames";
import styles from "./index.module.scss";
import CommentGroup from "../../../ADP/CommentGroup";
import PageMarker from "../../../ADP/PageMarker";
import TextArea from "../../../TextArea";
import TextGrid from "../../../TextGrid";
import CheckboxGroup from "../../../CheckboxGroup";
import RadioGroup from "../../../RadioGroup";
import { Text } from "../../../Type";
import Tooltip from "../../../Tooltip";
import ToggleAllUnitsButton from "../../../ToggleButton/ToggleAllUnitsButton";
import Modal from "../../../Modal";
import Button from "../../../Button";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import TextLink from "../../../TextLinks";
import { useToggleAllUnits } from "../../../../hooks/useToggleAllUnits";
import MeasurementInput from "../../../MeasurementInput";
import UploadDocumentsModal from "../../../ADP/UploadDocumentsModal";
import {
  customModificationsOptions,
  reasonOptions,
} from "../../../../utils/labelMap/LabelMap";
import { nonSpecialCharacterOptions } from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useTriggerOnFieldChange } from "../../../../hooks/useTriggerOnFieldChange";

const Section2a = () => {
  const { state: { user, externalUrls }, } = useStoreContext();
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const { clientFile, formMode: mode } = useClientFileContext();

  const isSalesRep = user?.role.name === "Sales Rep";
  const fileStatus = clientFile.status;

  const { trigger, setValue, getValues, clearErrors } = useFormContext();

  // #region options
  const baseDeviceOptions = [
    {
      label: "Adult Wheeled Walker Type 1",
      value: "awwt1",
      tooltip: (
        <Tooltip
          id="awwt1-tooltip"
          content="Walker type most suitable for use on hard, flat surfaces. ADP price includes frame, two or four small wheels (less than 6&Prime; diameter) and braking system (if available). See Ambulation Aids Options Chart if heavy duty frame and/or forearm attachments required.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Wheeled Walker Type 2",
      value: "awwt2",
      tooltip: (
        <Tooltip
          id="awwt2-tooltip"
          content="Walker type most suitable for use on hard, flat surfaces. ADP price includes frame, four small wheels (less than 6&Prime; diameter), hand braking system, seat, and back support. See Ambulation Aids Options Chart if heavy duty frame and/or forearm attachments required.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Wheeled Walker Type 3",
      value: "awwt3",
      tooltip: (
        <Tooltip
          id="awwt3-tooltip"
          content="Walker type most suitable for active use on most terrains. ADP price includes frame, four large wheels (6&Prime;/8&Prime; diameter), hand braking system, seat, and back support. See Ambulation Aids Options Chart if heavy duty frame and/or forearm attachments required.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Specific Wheeled Walker Type 1",
      value: "pswwt1",
      tooltip: (
        <Tooltip
          id="pswwt1-tooltip"
          content="Walker type most suitable for use on hard, flat surfaces. ADP price includes frame, two or four small wheels, and braking system (if available). See Ambulation Aids Options Chart for adolescent sizes.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Specific Wheeled Walker Type 2",
      value: "pswwt2",
      tooltip: (
        <Tooltip
          id="pswwt2-tooltip"
          content="Walker type most suitable for active use on most terrains. ADP price includes all components and options.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Specific Wheeled Walker Walking Frame",
      value: "pswwwf",
      tooltip: (
        <Tooltip
          id="pswwf-tooltip"
          content="ADP price includes frame, four wheels, hand grips (including arm supports), and built in sitting support (e.g. sling/saddle seat). See Ambulation Aids Options Chart for adolescent sizes.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Standing Frame Type 1",
      value: "psft1",
      tooltip: (
        <Tooltip
          id="psft1-tooltip"
          content="ADP price includes frame, positioning straps, trunk, pelvic, knee and foot supports, and tray. See Ambulation Aids Options Chart for adolescent sizes.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Standing Frame Type 2",
      value: "psft2",
      tooltip: (
        <Tooltip
          id="psft2-tooltip"
          content="ADP price includes frame, positioning straps, trunk, pelvic, knee and foot supports, and tray. See Ambulation Aids Options Chart for adolescent sizes.
          Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Forearm Crutches",
      value: "forearm",
    },
    {
      label: "None",
      value: "none",
    },
  ];

  const moveThroughoutOptions = [
    {
      label: "Yes",
      value: "mt-yes",
    },
    { label: "No", value: "mt-no" },
    { label: "N/A", value: "mt-na" },
  ];

  const moveBeyondOptions = [
    {
      label: "Yes",
      value: "mb-yes",
    },
    { label: "No", value: "mb-no" },
    { label: "N/A", value: "mb-na" },
  ];

  const inaccessibleOptions = [
    {
      label: "Yes",
      value: "in-yes",
    },
    { label: "No", value: "in-no" },
    { label: "N/A", value: "in-na" },
  ];

  const independentlyMobileOptions = [
    {
      label: "Yes",
      value: "im-yes",
    },
    { label: "No", value: "im-no" },
    { label: "N/A", value: "im-na" },
  ];

  const forearmCrutchesOptions = [
    {
      label: "Yes",
      value: "fc-yes",
    },
    { label: "No", value: "fc-no" },
    { label: "N/A", value: "fc-na" },
  ];

  const standingFrameOptions = [
    {
      label: "Yes",
      value: "sf-yes",
    },
    { label: "No", value: "sf-no" },
    { label: "N/A", value: "sf-na" },
  ];

  const handGripOptions = [
    {
      label: "None",
      value: "hg-none",
    },
    {
      label: "Standard",
      value: "hg-standard",
    },
    {
      label: "Anatomical",
      value: "hg-anatomical",
    },
  ];

  const forearmOptions = [
    {
      label: "One",
      value: "fa-one",
    },
    {
      label: "Two",
      value: "fa-two",
    },
  ];

  const brakeOptions = [
    {
      label: "None",
      value: "brake-none",
    },
    {
      label: "Push-To-Lock",
      value: "brake-pushToLock",
    },
    {
      label: "Auto Stop",
      value: "brake-auto-stop",
    },
  ];

  const brakeTypeOptions = [
    {
      label: "None",
      value: "brakeType-none",
    },
    {
      label: "Bilateral",
      value: "brakeType-bilateral",
    },
    {
      label: "One Hand",
      value: "brakeType-oneHand",
    },
  ];

  const numberOfWheelsOptions = [
    {
      label: "Two",
      value: "nw-two",
    },
    {
      label: "Three",
      value: "nw-three",
    },
    {
      label: "Four",
      value: "nw-four",
    },
  ];

  const wheelSizeOptions = [
    {
      label: "4-6 inches",
      value: "ws-4to6",
    },
    {
      label: "6-8 inches",
      value: "ws-8to8",
    },
    {
      label: "8-10 inches",
      value: "ws-8to10",
    },
  ];

  const backSupportOptions = [
    {
      label: "Yes",
      value: "bs-yes",
    },
    {
      label: "No",
      value: "bs-no",
    },
  ];

  const additionalAdpFundedOptions = [
    {
      label: "Adolescent Size Paediatric Specific Wheeled Walker",
      value: "adolescent-size-paediatric-specific",
    },
    {
      label: "Adolescent Size Paediatric Wheeled Walker – Walking Frame",
      value: "adolescent-size-paediatric-wheeled",
    },
    {
      label: "Adolescent Size Paediatric Standing Frame",
      value: "adolescent-size-paediatric-standing",
    },
  ];

  const replacementOptions = [
    {
      label:
        "Change in applicant’s mobility status - previously ADP funded equipment no longer meeting basic mobility needs as defined by ADP for funding purposes",
      value: "2a-change-in-status",
    },
    {
      label:
        "Change in applicant’s body size - previously ADP funded equipment is either too large or too small",
      value: "2a-change-in-size",
    },
    {
      label: "Previously ADP funded equipment is worn out",
      value: "2a-worn-out",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach vendor quote and/or copies of repair bills for wheeled
          walkers and wheelchairs only.
        </TextLink>
      ),
    },
    {
      label: "Special circumstances - none of the above",
      value: "2a-special-circumstances",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach letter of rationale.
        </TextLink>
      ),
    },
  ];

  // LOGIC for Units Toggle
  const { selectedUnit, toggleAllUnits } = useToggleAllUnits([
    {
      name: "section2a.seat-height-units",
      valuePrefix: "2a-seatHeight-",
      type: "length",
    },
    {
      name: "section2a.push-handle-units",
      valuePrefix: "2a-pushHandle-",
      type: "length",
    },
    {
      name: "section2a.width-between-units",
      valuePrefix: "2a-widthBetween-",
      type: "length",
    },
    {
      name: "section2a.client-weight-units",
      valuePrefix: "2a-clientWeight-",
      type: "weight",
    },
  ]);

  const [prescriptionDetailsRequired, setPrescriptionDetailsRequired] =
    useState<boolean>(true);

  // LOGIC for Select only one of Walker/Crutches/Frame
  const [prevDevices, setPrevDevices] = useState(
    useWatch({ name: "section2a.base-device" })
  );

  const reasonForApplication2a = useWatch({
    name: "section2a.reason-for-application",
  });
  const currDevices = useWatch({ name: "section2a.base-device" });
  useEffect(() => {
    if (currDevices !== prevDevices) {
      // Only one walker can be selected
      const walkers = ["awwt1", "awwt2", "awwt3", "pswwt1", "pswwt2", "pswwwf"];
      const prevWalkersIntersection = Array.isArray(prevDevices)
        ? prevDevices?.filter((x) => walkers.includes(x))
        : [];
      const currWalkersIntersection = Array.isArray(currDevices)
        ? currDevices?.filter((x) => walkers.includes(x))
        : [];
      if (prevDevices && currWalkersIntersection?.length >= 2) {
        setValue(
          "section2a.base-device",
          currDevices?.filter((x) => !prevWalkersIntersection.includes(x))
        );
      }

      // Only one frame can be selected
      const frames = ["psft1", "psft2"];
      const prevFramesIntersection = Array.isArray(prevDevices)
        ? prevDevices?.filter((x) => frames.includes(x))
        : [];
      const currFramesIntersection = Array.isArray(currDevices)
        ? currDevices?.filter((x) => frames.includes(x))
        : [];
      if (prevDevices && currFramesIntersection?.length >= 2) {
        setValue(
          "section2a.base-device",
          currDevices?.filter((x) => !prevFramesIntersection.includes(x))
        );
      }

      // If none is selected, clear others
      if (
        prevDevices &&
        !prevDevices.includes("none") &&
        currDevices?.length >= 2 &&
        currDevices?.includes("none")
      ) {
        setValue(
          "section2a.base-device",
          currDevices?.filter((x) => ["none"].includes(x))
        );
      }

      // If none was selected and something else is selected, clear none
      if (
        prevDevices &&
        prevDevices.includes("none") &&
        currDevices?.length >= 2
      ) {
        setValue(
          "section2a.base-device",
          currDevices?.filter((x) => !["none"].includes(x))
        );
      }

      setPrevDevices(currDevices);
    }
    // LOGIC for conditional requiredText for "Prescription Details"
    // PSWWWF is the only walker that shouldn't trigger required fields
    const requiredWalkers = ["awwt1", "awwt2", "awwt3", "pswwt1", "pswwt2"];
    if (reasonForApplication2a === "modifications-previous") {
      setPrescriptionDetailsRequired(false);
    } else if (
      Array.isArray(currDevices) &&
      currDevices?.some((device) => requiredWalkers.includes(device))
    ) {
      setPrescriptionDetailsRequired(true);
    } else {
      setPrescriptionDetailsRequired(false);
    }
  }, [
    setPrescriptionDetailsRequired,
    reasonForApplication2a,
    currDevices,
    prevDevices,
    setValue,
  ]);

  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.replacement-devices");
  useTriggerOnFieldChange("section2a.base-device", "section2a.hand-grips");
  useTriggerOnFieldChange("section2a.base-device", "section2a.brakes");
  useTriggerOnFieldChange("section2a.base-device", "section2a.brake-type");
  useTriggerOnFieldChange("section2a.base-device", "section2a.number-of-wheels");
  useTriggerOnFieldChange("section2a.base-device", "section2a.wheel-size");
  useTriggerOnFieldChange("section2a.base-device", "section2a.back-support");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.hand-grips");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.brakes");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.brake-type");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.number-of-wheels");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.wheel-size");
  useTriggerOnFieldChange("section2a.reason-for-application", "section2a.back-support");

  // LOGIC for Popup Modal if Type 3 Walker is selected
  const [openWalkerModal, setOpenWalkerModal] = useState(false);
  useEffect(() => {
    if (
      Array.isArray(currDevices) &&
      currDevices?.includes("awwt3") &&
      (!Array.isArray(prevDevices) ||
        (Array.isArray(prevDevices) && !prevDevices?.includes("awwt3")))
    ) {
      setOpenWalkerModal(true);
    }
    setPrevDevices(currDevices);
  }, [currDevices, prevDevices]);

  // LOGIC for popup for additional documents: https://app.asana.com/0/0/1200354578179107/f
  const [prevReplacementDevices, setPrevReplacementDevice] = useState(
    useWatch({ name: "section2a.replacement-devices" })
  );
  const currReplacementDevice = useWatch({
    name: "section2a.replacement-devices",
  });

  const [openPreviouslyFundedModal, setOpenPreviouslyFundedModal] =
    useState(false);
  const [openSpecialCircumstancesModal, setOpenSpecialCircumstancesModal] =
    useState(false);
  useEffect(() => {
    if (
      Array.isArray(currReplacementDevice) &&
      currReplacementDevice?.includes("2a-worn-out") &&
      (!Array.isArray(prevReplacementDevices) ||
        (Array.isArray(prevReplacementDevices) &&
          !prevReplacementDevices?.includes("2a-worn-out")))
    ) {
      setOpenPreviouslyFundedModal(true);
    }
    if (
      Array.isArray(currReplacementDevice) &&
      currReplacementDevice?.includes("2a-special-circumstances") &&
      (!Array.isArray(prevReplacementDevices) ||
        (Array.isArray(prevReplacementDevices) &&
          !prevReplacementDevices?.includes("2a-special-circumstances")))
    ) {
      setOpenSpecialCircumstancesModal(true);
    }
    setPrevReplacementDevice(currReplacementDevice);
  }, [currReplacementDevice, prevReplacementDevices]);

  // LOGIC for clinical rationale textbox show/hide
  const customModificationsObject = useWatch({
    name: "section2a.custom-modifications-required",
  });
  const checkCustomModifications = customModificationsObject === "cm-yes";

  // Clear errors for clinical rationale field when it is hidden
  useEffect(() => {
    if (!checkCustomModifications) {
      clearErrors("section2a.custom-modifications-clinical-rationale");
    }
  }, [checkCustomModifications, clearErrors]);

  // LOGIC for conditional requiredText in replacement-devices
  const currIsReplacementDevicesRequired =
    reasonForApplication2a !== "first-access" &&
    reasonForApplication2a !== "another-type";
  // Need to use useState + useEffect to trigger re-renders
  const [isReplacementDevicesRequired, setIsReplacementDevicesRequired] =
    useState(currIsReplacementDevicesRequired);
  useEffect(() => {
    setIsReplacementDevicesRequired(currIsReplacementDevicesRequired);
  }, [currIsReplacementDevicesRequired, trigger]);

  return (
    <>
      <PageMarker page={3} total={13} />
      <CommentGroup
        section="section2a"
        id="1-base-device"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2a"
          name="base-device"
          label="Base Device (check one walker and/or forearm crutches and/or one paediatric standing frame)"
          options={baseDeviceOptions}
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="base-device-tooltip"
              content="Please refer to the ADP product manual for more details."
              links={[
                {
                  id: "link-1",
                  text: "ADP Manual",
                  href: externalUrls.mobility_devices_product_manual,
                },
              ]}
            />
          }
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="2-reason-for-application"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2a"
          name="reason-for-application"
          label="Reason for Application"
          options={reasonOptions}
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="3-replacement-devices"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2a"
          options={replacementOptions}
          name="replacement-devices"
          label="Replacement Device(s) and/or Modifications Required Due To: (check as appropriate)"
          requiredText={
            isReplacementDevicesRequired
              ? "This is a required field."
              : undefined
          }
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="replacement-devices-tooltip"
              content="If you are selecting 'Previously funded ADP device is worn out', please provide a repair quote (Unless only seating needs to be replaced, then a quote is not required)."
            />
          }
        />
        <Modal
          title="Supporting Documents"
          isOpen={isUploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
          }}
        >
          <UploadDocumentsModal
            clientFileId={clientFile.id}
            setUploadModalOpen={setUploadModalOpen}
          />
        </Modal>
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="4-replacement"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text component="span" size="large" weight="bold">
          Confirmation of Applicant’s Eligibility for Ambulation Aids: (answer
          required for each statement)
        </Text>
        <div className={styles.numberedRadioGroups}>
          <Text className={styles.numberBullet} weight="bold">
            1.
          </Text>
          <RadioGroup
            section="section2a"
            name="move-throughout-residence"
            description="Applicant requires the prescribed device in order to move throughout their place of residence."
            options={moveThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            2.
          </Text>
          <RadioGroup
            section="section2a"
            name="move-beyond-residence"
            description="Applicant requires the prescribed device in order to move beyond their place of residence."
            options={moveBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            3.
          </Text>
          <RadioGroup
            section="section2a"
            name="move-through-inaccessible"
            description="Applicant requires the prescribed device to access wheelchair inaccessible areas in their place of residence."
            options={inaccessibleOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            4.
          </Text>
          <RadioGroup
            section="section2a"
            name="independently-mobile"
            description="Applicant is independently mobile with the prescribed device."
            options={independentlyMobileOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            5.
          </Text>
          <RadioGroup
            section="section2a"
            name="requires-forearm-crutches"
            description="Applicant requires forearm crutches."
            options={forearmCrutchesOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            6.
          </Text>
          <RadioGroup
            section="section2a"
            name="specific-standing-frame"
            description="Applicant requires a paediatric specific standing frame."
            options={standingFrameOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <PageMarker page={4} total={13} />

      <CommentGroup
        section="section2a"
        id="5-prescription-details"
        className={classNames(styles.marginAfterPM)}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text size="large" weight="bold">
          Prescription Details for Wheeled Walker Only (answers required for all
          specifications){" "}
          <Tooltip
            id="prescription-details-tooltip"
            content="See product manual for details about all generic device types"
            links={[
              {
                id: "link-1",
                text: "Product Manual",
                href: externalUrls.mobility_devices_product_manual,
              },
            ]}
          />
        </Text>

        <Text size="large">Toggle All Units</Text>
        <ToggleAllUnitsButton
          section="section2a"
          setMetric={() => {
            toggleAllUnits("metric");
          }}
          setImperial={() => {
            toggleAllUnits("imperial");
          }}
          selectedUnit={selectedUnit}
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2a"
          formFieldId="seat-height"
          label="Seat Height"
          type="length"
          valuePrefix="2a-seatHeight"
          naOption
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2a"
          formFieldId="push-handle-height"
          formFieldUnitsId="push-handle-units"
          label="Push Handle Height"
          type="length"
          valuePrefix="2a-pushHandle"
          readOnly={mode !== "edit"}
        />
        <RadioGroup
          section="section2a"
          name="hand-grips"
          description="Hand Grips"
          options={handGripOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
        />
        <RadioGroup
          section="section2a"
          name="forearm-attachments"
          description="Forearm Attachments"
          options={forearmOptions}
          horizontal
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep &&
            getValues("section2a.forearm-attachments") === "fa-two" && (
              <Tooltip
                id="forearm-attachments-tooltip"
                content="Two attachments are available for additional funding under funding code AA 1."
              />
            )
          }
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2a"
          formFieldId="width-push-handles"
          formFieldUnitsId="width-between-units"
          label="Width Between Push Handles"
          type="length"
          valuePrefix="2a-widthBetween"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="width-push-handles-tooltip"
                content="If width between handles exceeds 18 inches, additional funding is available under funding code AA8."
              />
            )
          }
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2a"
          formFieldId="client-weight"
          label="Client Weight"
          type="weight"
          valuePrefix="2a-clientWeight"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="client-weight-tooltip"
                content="Additional funding is available under funding codes AA2 for 275-350 lbs, Code AA3 for 350-400 lbs, and Code AA4 for 400 lbs plus."
              />
            )
          }
        />
        <RadioGroup
          section="section2a"
          name="brakes"
          description="Brakes"
          options={brakeOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
        />
        <RadioGroup
          section="section2a"
          name="brake-type"
          description="Brake Type"
          options={brakeTypeOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep &&
            getValues("section2a.brake-type") === "brakeType-bilateral" && (
              <Tooltip
                id="brake-type-tooltip"
                content="Bilateral Brake is available for additional funding under funding code AA9"
              />
            )
          }
        />
        <RadioGroup
          section="section2a"
          name="number-of-wheels"
          description="Number of Wheels"
          options={numberOfWheelsOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
        />
        <RadioGroup
          section="section2a"
          name="wheel-size"
          description="Wheel Size"
          options={wheelSizeOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
        />
        <RadioGroup
          section="section2a"
          name="back-support"
          description="Back Support"
          options={backSupportOptions}
          horizontal
          requiredText={
            prescriptionDetailsRequired ? "This field is required." : undefined
          }
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="6-additional-adp"
        className={styles.margin}
      >
        <CheckboxGroup
          section="section2a"
          options={additionalAdpFundedOptions}
          name="additional-adp-funded-options"
          label="Additional ADP Funded Options Required for Prescribed Device (if applicable check one or more)"
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="7-non-adp-funded"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2a"
          id="non-adp-funded-options-prescribed"
          maxInputs={9}
          maxLength={35}
          label="Non ADP Funded Options Prescribed"
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="8-setup-instruction"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2a"
          id="setup-instructions-for-vendor"
          label="Set Up Instructions for Vendor"
          maxInputs={18}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2a"
        id="9-custom-modifications"
        className={styles.margin}
        commentBoxPosition={!checkCustomModifications ? "top" : undefined}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2a"
          name="custom-modifications-required"
          label="Custom Modifications Required"
          options={customModificationsOptions}
          horizontal
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="custom-modifications-tooltip"
              content="If custom modifications are made on a device, a custom modification quote is required that is specifically associated with the custom work. Please include the cost for parts and labour, as well as, clinical rationale that led to this custom modification being required. "
            />
          }
        />
        {checkCustomModifications && (
          <TextArea
            section="section2a"
            id="custom-modifications-clinical-rationale"
            label="Custom Modifications Clinical Rationale"
            description="The authorizer must provide clinical rationale to support the request in the space below and attach a vendor quote that provides
          a breakdown of the cost of labour (not to exceed $40.00/hour) and parts."
            placeholder="Custom modifications required - clinical rationale"
            maxLength={1220}
            className={styles.paddingNoHorizontal}
            readOnly={mode !== "edit"}
            fieldOptions={nonSpecialCharacterOptions}
          />
        )}
      </CommentGroup>
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openWalkerModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenWalkerModal(false)}
        >
          <div className={styles.modalText}>
            If you have a scooter/power wheelchair, and you are applying for a
            Type 3 walker in this section, it is likely that this will not be
            funded by ADP. See section 600 of the ADP Policy and Procedures
            Manual
            <TextLink
              to={
                externalUrls.mobility_devices_policy_and_administration_manual
              }
              externalURL
            >
              ADP Policy and Procedures Manual
            </TextLink>
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenWalkerModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openPreviouslyFundedModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenPreviouslyFundedModal(false)}
        >
          <div className={styles.modalText}>
            Please provide a repair quote (Unless only seating needs to be
            replaced, then a quote is not required).
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenPreviouslyFundedModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openSpecialCircumstancesModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenSpecialCircumstancesModal(false)}
        >
          <div className={styles.modalText}>
            Please upload a letter for ADP describing the special circumstances.
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button
              primary
              onClick={() => setOpenSpecialCircumstancesModal(false)}
            >
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section2a;
